import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { toast } from "sonner";
import { REGOLAMENTO } from "@/regolamento";
import { SponsorStrip } from "@/components/SponsorStrip";
import { MobileNav } from "@/components/MobileNav";

const ACCENTS = {
  gold: { text: "text-[#b45309]", bg: "bg-[#f59e0b]", ring: "#f59e0b" },
  silver: { text: "text-[#0284c7]", bg: "bg-[#38bdf8]", ring: "#38bdf8" },
};

function computeStandings(teams, rounds) {
  const rows = {};
  teams.forEach((t) => {
    rows[t] = { team: t, played: 0, won: 0, drawn: 0, lost: 0, sf: 0, sa: 0, pts: 0 };
  });
  rounds.forEach((r) =>
    r.matches.forEach((m) => {
      if (!m.played || !rows[m.home] || !rows[m.away]) return;
      const h = rows[m.home];
      const a = rows[m.away];
      h.played += 1;
      a.played += 1;
      h.sf += m.home_sets;
      h.sa += m.away_sets;
      a.sf += m.away_sets;
      a.sa += m.home_sets;
      if (m.home_sets === m.away_sets) {
        h.drawn += 1;
        a.drawn += 1;
        h.pts += 1;
        a.pts += 1;
      } else {
        const winner = m.home_sets > m.away_sets ? h : a;
        const loser = m.home_sets > m.away_sets ? a : h;
        const loserScore = Math.min(m.home_sets, m.away_sets);
        winner.won += 1;
        loser.lost += 1;
        winner.pts += loserScore === 0 ? 3 : 2;
      }
    })
  );
  return Object.values(rows).sort(
    (x, y) => y.pts - x.pts || y.sf - y.sa - (x.sf - x.sa) || x.team.localeCompare(y.team)
  );
}

function MatchRow({ match, accent, teamInfo }) {
  const home = teamInfo[match.home] || {};
  const away = teamInfo[match.away] || {};
  return (
    <div
      data-testid={`match-row-${match.id}`}
      className="p-4 bg-[#e3edf6] rounded border border-[#e2e8f0] flex flex-col md:flex-row justify-between md:items-center gap-2"
    >
      <span
        className={`font-ui text-xs whitespace-nowrap ${
          match.date_label.startsWith("DOM") ? (accent === "gold" ? "text-[#0891b2]" : "text-[#dc2626]") : ACCENTS[accent].text
        }`}
      >
        {match.date_label} • {match.time}
      </span>
      <div className="text-sm flex-1 md:px-4 flex flex-col md:flex-row md:items-center md:justify-center gap-1 md:gap-3">
        <span className="inline-flex items-center gap-1.5 font-medium text-[#0f172a]">
          {home.logo && (
            <img
              src={home.logo}
              alt=""
              data-testid={`match-logo-home-${match.id}`}
              className="w-8 h-8 rounded object-contain"
            />
          )}
          <span>
            {match.home}
            {home.venue && (
              <span data-testid={`match-venue-${match.id}`} className="block text-[10px] font-normal text-[#64748b] leading-tight">
                {home.venue}
              </span>
            )}
          </span>
        </span>
        <span className="text-[#64748b] font-mono-x text-xs">vs</span>
        <span className="inline-flex items-center gap-1.5 font-medium text-[#0f172a]">
          {away.logo && (
            <img
              src={away.logo}
              alt=""
              data-testid={`match-logo-away-${match.id}`}
              className="w-8 h-8 rounded object-contain"
            />
          )}
          {match.away}
        </span>
      </div>
      {match.played ? (
        <span data-testid={`match-result-${match.id}`} className="font-ui text-sm text-[#0f172a] bg-[#e2e8f0] px-3 py-1 rounded whitespace-nowrap">
          {match.home_sets} - {match.away_sets}
        </span>
      ) : match.postponed ? (
        <span data-testid={`match-postponed-${match.id}`} className="font-ui text-xs text-[#b45309] bg-[#fef3c7] border border-[#fcd34d] px-3 py-1 rounded whitespace-nowrap text-center">
          Rinviata
          {match.recovery_date && (
            <span data-testid={`match-recovery-${match.id}`} className="block text-[10px] text-[#92400e]">
              Recupero: {new Date(match.recovery_date + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" })}
            </span>
          )}
        </span>
      ) : (
        <span className="font-mono-x text-xs text-[#475569] whitespace-nowrap">In attesa risultato</span>
      )}
    </div>
  );
}

const OUTLET_LOGOS = {
  perugiatoday: "/logos/perugiatoday.png",
  ternitoday: "/logos/ternitoday.png",
};

export default function Home() {
  const [leagues, setLeagues] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [press, setPress] = useState([]);
  const [ytVideo, setYtVideo] = useState(null);
  const [league, setLeague] = useState("gold");
  const [gironeIdx, setGironeIdx] = useState(0);
  const [giornata, setGiornata] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api
      .get("/leagues")
      .then((res) => setLeagues(res.data))
      .catch(() => toast.error("Impossibile caricare i calendari"));
    api
      .get("/clubs")
      .then((res) => setClubs(res.data))
      .catch(() => {});
    api
      .get("/press")
      .then((res) => setPress(res.data))
      .catch(() => {});
    api
      .get("/youtube/latest")
      .then((res) => setYtVideo(res.data))
      .catch(() => {});
  }, []);

  const teamInfo = useMemo(() => {
    const map = {};
    clubs.forEach((c) => {
      const overrides = {};
      (c.team_venues || []).forEach((tv) => (overrides[tv.team] = tv.venue));
      c.teams.forEach((t) => (map[t.name] = { logo: c.logo || null, venue: overrides[t.name] || c.venue || null }));
    });
    return map;
  }, [clubs]);

  const gironi = leagues ? leagues[league].gironi : [];
  const girone = gironi[Math.min(gironeIdx, gironi.length - 1)];

  // punta di default all'ultima giornata il cui weekend e' iniziato (o in corso)
  useEffect(() => {
    if (!girone) return;
    const today = new Date().toISOString().slice(0, 10);
    let best = girone.rounds[0]?.n ?? 1;
    for (const r of girone.rounds) {
      const first = r.matches.map((m) => m.date).sort()[0];
      if (first && first <= today) best = r.n;
    }
    setGiornata(best);
  }, [league, gironeIdx, leagues]);

  const round = girone?.rounds.find((r) => r.n === giornata) || girone?.rounds[0];
  const standings = useMemo(
    () => (girone ? computeStandings(girone.teams, girone.rounds) : []),
    [girone]
  );
  const playedCount = useMemo(
    () => (girone ? girone.rounds.reduce((n, r) => n + r.matches.filter((m) => m.played).length, 0) : 0),
    [girone]
  );

  const selectLeague = (l) => {
    setLeague(l);
    setGironeIdx(0);
    setGiornata(1);
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Messaggio inviato. L'organizzazione ti risponderà presto.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err, "Invio non riuscito. Riprova."));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#e3edf6] text-[#0f172a] min-h-screen">
      {/* NAV */}
      <nav className="border-b border-[#e2e8f0] px-6 md:px-8 py-5 sticky top-0 bg-[#e3edf6]/90 backdrop-blur-md z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="#top" data-testid="nav-logo" className="flex items-center gap-3">
            <img src="/logo-coppa.png" alt="Coppa dei Club" className="h-8 w-auto" />
            <span className="text-[#0284c7] font-mono-x text-xs">/ Hub</span>
          </a>
          <div className="flex items-center gap-2 text-xs font-ui text-[#475569]">
            <a href="#calendari" data-testid="nav-calendari" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0f172a] hover:bg-[#38bdf8] transition-all rounded font-semibold border border-[#cbd5e1]">
              Calendari Gare
            </a>            <Link to="/circoli" data-testid="nav-circoli" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0f172a] hover:bg-[#38bdf8] transition-all rounded font-semibold border border-[#cbd5e1]">
              Circoli
            </Link>
            <Link to="/galleria" data-testid="nav-galleria" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0f172a] hover:bg-[#38bdf8] transition-all rounded font-semibold border border-[#cbd5e1]">
              Galleria foto
            </Link>

            <a href="#regolamento" data-testid="nav-regolamento" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0f172a] hover:bg-[#38bdf8] transition-all rounded font-semibold border border-[#cbd5e1]">
              Regolamento
            </a>
            <Link to="/modulistica" data-testid="nav-modulistica" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0f172a] hover:bg-[#38bdf8] transition-all rounded font-semibold border border-[#cbd5e1]">
              Modulistica
            </Link>
            <Link
              to="/admin"
              data-testid="nav-admin-login"
              className="hidden sm:inline-block px-4 py-2 bg-[#e2e8f0] text-[#0f172a] hover:bg-[#38bdf8] hover:text-[#0f172a] transition-all rounded font-semibold border border-[#cbd5e1]"
            >
              Login Admin
            </Link>
            <MobileNav prefix="" active="home" />
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header id="top" className="relative py-16 md:py-20 px-6 md:px-8 border-b border-[#e2e8f0] overflow-hidden">
        <img
          src="/hero-padel.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e3edf6_0%,rgba(227,237,246,0.7)_45%,rgba(227,237,246,0)_70%)]"></div>
        <div className="relative z-10 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 text-xs font-mono-x text-[#0284c7] mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-[#38bdf8]"></span>
              COPPA DEI CLUB • GIRONE UMBRIA
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.12] text-[#0f172a] mb-6">
              <span className="ln"><span>CAMPIONATO REGIONALE PADEL</span></span>
              <span className="ln"><span className="text-[#0284c7]">MSP ITALIA 2026/27</span></span>
            </h1>
            <p className="text-base md:text-lg text-[#475569] max-w-xl mb-8 leading-relaxed font-editorial">
              Tutti i turni, gli accoppiamenti e i risultati dei gironi Gold e Silver sempre aggiornati
              per giocatori e responsabili dei circoli.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#calendari"
                data-testid="hero-cta-calendari"
                className="px-6 py-3 bg-[#38bdf8] text-[#0f172a] font-bold text-xs uppercase tracking-wider rounded hover:bg-[#7dd3fc] transition-colors"
              >
                Visualizza Calendari
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div
              className="f-wipe rounded-2xl border border-[#cbd5e1] lg:aspect-[4/3] p-6 relative overflow-hidden shadow-2xl bg-white"
            >
              <div className="relative z-10 flex flex-col justify-between h-full">
                <div className="flex justify-between items-start">
                  <span className="font-ui text-[11px] text-[#0284c7] uppercase tracking-widest bg-white/80 px-3 py-1 rounded">
                    Stagione 2026-27
                  </span>
                  <span className="font-ui text-xs text-[#0f172a]">12 Gironi Ufficiali</span>
                </div>
                <div className="flex-1 min-h-0 flex items-center justify-center py-3">
                  <img
                    src="/edizione-12.png"
                    alt="12esima Edizione - Coppa dei Club MSP Italia"
                    data-testid="hero-edizione-img"
                    className="max-h-40 lg:max-h-full w-auto object-contain"
                  />
                </div>
                <div className="space-y-2">
                  <div className="bg-white/85 p-3 rounded border border-[#e2e8f0] flex justify-between items-center text-xs">
                    <img src="/logos/girone-gold.png" alt="Serie Gold" className="h-6 w-auto" />
                    <span className="text-[#b45309] font-ui">5 Gironi • 37 Squadre</span>
                  </div>
                  <div className="bg-white/85 p-3 rounded border border-[#e2e8f0] flex justify-between items-center text-xs">
                    <img src="/logos/girone-silver.png" alt="Serie Silver" className="h-6 w-auto" />
                    <span className="text-[#0284c7] font-ui">7 Gironi • 56 Squadre</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* SPONSOR */}
      <SponsorStrip />

      {/* MARQUEE */}
      <section className="py-3 bg-[#d9e6f1] border-b border-[#e2e8f0] overflow-hidden" aria-hidden="true">
        <div className="mq-track font-mono-x text-xs uppercase text-[#64748b] flex items-center gap-10">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-10">
              <span>• COPPA DEI CLUB 2026-27</span>
              <span className="text-[#0f172a]">• AGGIORNAMENTO CONTINUO TABELLONI</span>
              <span>• GIRONI GOLD &amp; SILVER</span>
              <span className="text-[#0f172a]">• AREA REFERTI GESTORI</span>
            </div>
          ))}
        </div>
      </section>

      {/* CALENDARI */}
      <section id="calendari" className="py-24 px-6 md:px-8 border-b border-[#e2e8f0]">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <span className="font-mono-x text-xs text-[#0284c7] tracking-widest uppercase block mb-1">
                Calendario Ufficiale
              </span>
              <h2 className="font-display text-3xl md:text-4xl text-[#0f172a]">Gironi Gold &amp; Silver</h2>
            </div>
          </div>

          {/* League toggle */}
          <div className="flex gap-3 mb-6">
            <button
              data-testid="league-toggle-gold"
              onClick={() => selectLeague("gold")}
              className={`cursor-pointer px-4 py-2 rounded border transition-all flex items-center bg-white ${
                league === "gold"
                  ? "border-[#f59e0b] ring-2 ring-[#f59e0b]/40"
                  : "border-[#cbd5e1] hover:border-[#f59e0b]"
              }`}
            >
              <img src="/logos/girone-gold.png" alt="Girone Gold" data-testid="league-logo-gold" className="h-7 w-auto" />
            </button>
            <button
              data-testid="league-toggle-silver"
              onClick={() => selectLeague("silver")}
              className={`cursor-pointer px-4 py-2 rounded border transition-all flex items-center bg-white ${
                league === "silver"
                  ? "border-[#94a3b8] ring-2 ring-[#94a3b8]/40"
                  : "border-[#cbd5e1] hover:border-[#94a3b8]"
              }`}
            >
              <img src="/logos/girone-silver.png" alt="Girone Silver" data-testid="league-logo-silver" className="h-7 w-auto" />
            </button>
          </div>

          {!leagues && (
            <div data-testid="leagues-loading" className="text-[#475569] font-mono-x text-xs py-12 text-center">
              Caricamento calendari...
            </div>
          )}

          {leagues && girone && (
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 md:p-6">
              {/* Girone selector */}
              <div className="flex flex-wrap gap-2 mb-6" data-testid="girone-selector">
                {gironi.map((g, i) => (
                  <button
                    key={g.name}
                    data-testid={`girone-btn-${g.name}`}
                    onClick={() => {
                      setGironeIdx(i);
                      setGiornata(1);
                    }}
                    className={`px-4 py-1.5 rounded text-xs font-ui border transition-all ${
                      i === gironeIdx
                        ? `${ACCENTS[league].bg} text-[#0f172a] border-transparent font-bold`
                        : "bg-[#e3edf6] text-[#475569] border-[#cbd5e1] hover:text-[#0f172a]"
                    }`}
                  >
                    Girone {g.name}
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-between border-b border-[#e2e8f0] pb-4 mb-6">
                <h3 className="flex items-center gap-3">
                  <img
                    src={league === "gold" ? "/logos/girone-gold.png" : "/logos/girone-silver.png"}
                    alt={league === "gold" ? "Girone Gold" : "Girone Silver"}
                    className="h-7 w-auto"
                  />
                  <span className={`font-bold text-lg ${ACCENTS[league].text} uppercase`}>
                    Calendario Gare • Girone {girone.name}
                  </span>
                </h3>
                <span className="font-mono-x text-xs text-[#475569]">Formula Round Robin</span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                {/* Matches */}
                <div className="lg:col-span-3">
                  <div className="flex flex-wrap gap-2 mb-4" data-testid="giornata-selector">
                    {girone.rounds.map((r) => (
                      <button
                        key={r.n}
                        data-testid={`giornata-btn-${r.n}`}
                        onClick={() => setGiornata(r.n)}
                        className={`px-3 py-1.5 rounded text-[11px] font-ui border transition-all ${
                          r.n === giornata
                            ? "bg-[#0f172a] text-white border-[#0f172a] font-bold"
                            : "bg-[#e3edf6] text-[#475569] border-[#cbd5e1] hover:text-[#0f172a]"
                        }`}
                      >
                        {r.n}° • {r.label}
                      </button>
                    ))}
                  </div>

                  {round && (
                    <div className="space-y-3">
                      {round.matches.map((m) => (
                        <MatchRow key={m.id} match={m} accent={league} teamInfo={teamInfo} />
                      ))}
                      {round.bye && (
                        <div
                          data-testid={`bye-note-${round.n}`}
                          className="p-3 rounded border border-dashed border-[#cbd5e1] text-xs font-ui text-[#64748b] text-center"
                        >
                          Riposa: {round.bye}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Standings */}
                <div className="lg:col-span-2">
                  <h4 className="font-mono-x text-xs text-[#475569] uppercase tracking-widest mb-3">
                    Classifica {playedCount === 0 && "• in attesa dei primi risultati"}
                  </h4>
                  <div data-testid="standings-table" className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-[#64748b] font-ui border-b border-[#e2e8f0]">
                          <th className="py-2 pr-2 font-normal">#</th>
                          <th className="py-2 pr-2 font-normal">Squadra</th>
                          <th className="py-2 px-1 font-normal text-center">G</th>
                          <th className="py-2 px-1 font-normal text-center">V</th>
                          <th className="py-2 px-1 font-normal text-center">N</th>
                          <th className="py-2 px-1 font-normal text-center">P</th>
                          <th className="py-2 px-1 font-normal text-center">Inc</th>
                          <th className="py-2 pl-1 font-normal text-right">Pt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {standings.map((row, i) => (
                          <tr
                            key={row.team}
                            data-testid={`standing-row-${i}`}
                            className="border-b border-[#e2e8f0] last:border-0"
                          >
                            <td className={`py-2 pr-2 font-ui ${i === 0 ? ACCENTS[league].text : "text-[#64748b]"}`}>
                              {i + 1}
                            </td>
                            <td className="py-2 pr-2 text-[#0f172a] font-medium">{row.team}</td>
                            <td className="py-2 px-1 text-center text-[#475569] font-ui">{row.played}</td>
                            <td className="py-2 px-1 text-center text-[#475569] font-ui">{row.won}</td>
                            <td className="py-2 px-1 text-center text-[#475569] font-ui">{row.drawn}</td>
                            <td className="py-2 px-1 text-center text-[#475569] font-ui">{row.lost}</td>
                            <td className="py-2 px-1 text-center text-[#475569] font-ui">
                              {row.sf}:{row.sa}
                            </td>
                            <td className={`py-2 pl-1 text-right font-ui font-bold ${ACCENTS[league].text}`}>
                              {row.pts}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-3 text-[10px] font-ui text-[#64748b] leading-relaxed">
                    Punteggio ufficiale: 4-0 vale 3 punti, 3-1 vale 2 punti, il pareggio 2-2 vale 1 punto
                    per squadra. A pari punti conta la differenza incontri.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* REGOLAMENTO */}
      <section id="regolamento" className="py-24 px-6 md:px-8 border-b border-[#e2e8f0] bg-[#dbe8f2]">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <span className="font-mono-x text-xs text-[#0284c7] uppercase tracking-widest block mb-2">
                Disposizioni Ufficiali
              </span>
              <h2 className="font-display text-3xl md:text-4xl text-[#0f172a]">Regolamento del Campionato</h2>
              <p className="text-sm text-[#475569] mt-3 max-w-xl leading-relaxed">
                Regolamento ufficiale Coppa dei Club 2026-27. Di seguito la sintesi dei 18 articoli: il
                documento PDF scaricabile è la versione integrale che fa fede.
              </p>
            </div>
            <a
              href="/regolamento-2026-27.pdf"
              download="Regolamento-Coppa-dei-Club-2026-27.pdf"
              data-testid="regolamento-download"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#38bdf8] text-[#0f172a] font-bold text-xs uppercase tracking-wider rounded hover:bg-[#7dd3fc] transition-colors whitespace-nowrap self-start md:self-auto"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 21h16" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Scarica il PDF
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {REGOLAMENTO.map((art) => (
              <div
                key={art.n}
                data-testid={`regolamento-art-${art.n}`}
                className="p-6 bg-white border border-[#e2e8f0] rounded-xl space-y-3 hover:border-[#cbd5e1] transition-colors"
              >
                <h3 className="font-bold text-[#0f172a] text-base">
                  <span className="text-[#0284c7] font-ui mr-2">Art. {art.n}</span>
                  {art.title}
                </h3>
                <ul className="space-y-2">
                  {art.points.map((p, i) => (
                    <li key={i} className="text-xs text-[#475569] leading-relaxed flex gap-2">
                      <span className="text-[#0284c7] mt-0.5 shrink-0">•</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-20 px-6 md:px-8 border-b border-[#e2e8f0]">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/galleria"
            data-testid="home-box-galleria"
            className="aspect-[4/3] rounded-2xl border border-[#e2e8f0] relative overflow-hidden group"
          >
            <img
              src="/teams/hangar-padel-orvieto.jpg"
              alt="Foto ufficiali delle squadre"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover opacity-80 transition-transform duration-300 group-hover:scale-105"
            />
            <span className="absolute bottom-2 left-2 text-[10px] font-ui text-[#0f172a] bg-white/80 px-2 py-0.5 rounded">
              Galleria foto
            </span>
            <span className="absolute top-2 right-2 text-[10px] font-ui font-semibold text-white bg-[#0284c7] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
              Sfoglia →
            </span>
          </Link>
          <div
            data-testid="home-box-rassegna"
            className="aspect-[4/3] rounded-2xl border border-[#e2e8f0] relative overflow-hidden bg-white flex flex-col"
          >
            <div className="px-4 pt-3 pb-2 border-b border-[#e2e8f0] flex items-center justify-between">
              <span className="text-[10px] font-ui text-[#0f172a] bg-[#e3edf6] px-2 py-0.5 rounded">
                Rassegna stampa
              </span>
              <span className="text-[10px] font-ui text-[#64748b]">{press.length} articoli</span>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5" data-testid="rassegna-list">
              {press.length === 0 && (
                <p className="text-[11px] text-[#64748b] font-ui text-center py-6">Nessun articolo pubblicato.</p>
              )}
              {press.slice(0, 2).map((a) => {
                const logo = OUTLET_LOGOS[a.outlet.toLowerCase()];
                return (
                  <a
                    key={a.id}
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`rassegna-article-${a.id}`}
                    className="flex items-start gap-3 px-2 py-2 rounded border border-transparent hover:border-[#e2e8f0] hover:bg-[#f8fafc] transition-all"
                  >
                    {logo ? (
                      <img src={logo} alt={a.outlet} className="h-4 w-auto object-contain mt-0.5 shrink-0 max-w-[84px]" />
                    ) : (
                      <span className="text-[9px] font-ui font-bold text-[#0284c7] uppercase shrink-0 mt-0.5">{a.outlet}</span>
                    )}
                    <span className="min-w-0">
                      <span className="block text-[11px] font-ui font-semibold text-[#0f172a] leading-snug line-clamp-2">
                        {a.title}
                      </span>
                      <span className="block text-[9px] font-ui text-[#64748b] mt-0.5">
                        {new Date(a.date).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                    </span>
                  </a>
                );
              })}
            </div>
            <Link
              to="/rassegna-stampa"
              data-testid="rassegna-archive-link"
              className="block px-4 py-2.5 border-t border-[#e2e8f0] text-center text-[11px] font-ui font-semibold text-[#0284c7] hover:bg-[#f0f9ff] transition-colors"
            >
              Archivio rassegna stampa →
            </Link>
          </div>
          <a
            href={ytVideo ? ytVideo.url : "https://www.youtube.com/watch?v=7Z5xFLYZ53M"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-box-youtube"
            className="aspect-[4/3] rounded-2xl border border-[#e2e8f0] relative overflow-hidden bg-white group flex flex-col"
          >
            <div className="flex items-center gap-2 px-4 pt-3 pb-2">
              <img src="/logos/noi-sport.png" alt="Noi Sport - very important player" className="h-8 w-auto object-contain" />
              <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] font-ui text-[#0f172a] bg-[#e3edf6] px-2 py-1 rounded">
                <svg className="w-4 h-4 text-[#dc2626]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 00.5 6.2 31.3 31.3 0 000 12a31.3 31.3 0 00.5 5.8 3 3 0 002.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 002.1-2.1A31.3 31.3 0 0024 12a31.3 31.3 0 00-.5-5.8zM9.6 15.6V8.4L15.8 12l-6.2 3.6z" />
                </svg>
                Segui il nostro canale YouTube
              </span>
            </div>
            <div className="relative flex-1 mx-4 mb-4 rounded-2xl overflow-hidden bg-[#0f172a]">
              {ytVideo ? (
                <>
                  <img
                    src={ytVideo.thumbnail}
                    alt={ytVideo.title}
                    data-testid="youtube-latest-thumb"
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-90 transition-transform duration-300 group-hover:scale-105"
                  />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-12 h-12 rounded-full bg-[#dc2626]/90 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                      <svg className="w-5 h-5 text-white ml-0.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                  </span>
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0f172a]/90 to-transparent px-3 pt-8 pb-2">
                    <span data-testid="youtube-latest-title" className="block text-[11px] font-ui font-semibold text-white leading-snug line-clamp-2">
                      {ytVideo.title}
                    </span>
                    <span className="block text-[9px] font-ui text-[#94a3b8] mt-0.5">
                      Ultimo video • {new Date(ytVideo.published).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  </span>
                </>
              ) : (
                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-ui text-[#94a3b8]">
                  Caricamento ultimo video...
                </span>
              )}
            </div>
          </a>
        </div>
      </section>

      {/* MEDIA PARTNER */}
      <section className="py-8 px-6 md:px-8 bg-white border-b border-[#e2e8f0]" data-testid="media-partner-strip">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-14">
          <span className="text-base md:text-lg font-ui font-bold text-[#0f172a] uppercase tracking-widest">
            Media Partner
          </span>
          <a
            href="https://www.ternitoday.it/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="media-partner-ternitoday"
            className="hover:opacity-80 transition-opacity"
          >
            <img src="/logos/ternitoday.png" alt="TerniToday" className="h-8 md:h-10 w-auto object-contain" />
          </a>
          <a
            href="https://www.perugiatoday.it/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="media-partner-perugiatoday"
            className="hover:opacity-80 transition-opacity"
          >
            <img src="/logos/perugiatoday.png" alt="PerugiaToday" className="h-8 md:h-10 w-auto object-contain" />
          </a>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contatti" className="py-24 px-6 md:px-8">
        <div className="max-w-6xl mx-auto bg-white border border-[#e2e8f0] rounded-2xl p-8 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <span className="font-ui text-xs text-[#0284c7] uppercase tracking-widest block mb-1">
                Area Gestori &amp; Club
              </span>
              <h2 className="font-display text-2xl text-[#0f172a] mb-3">Contatta l'Organizzazione</h2>
              <p className="text-xs text-[#475569] leading-relaxed font-ui mb-4">
                Usa questo modulo per richiedere assistenza all'organizzazione o le credenziali di accesso
                per l'aggiornamento dei tabelloni.
              </p>
              <a
                href="mailto:coppadeiclubumbria@gmail.com"
                data-testid="contact-email-link"
                className="inline-flex items-center gap-2 text-sm font-ui text-[#0284c7] hover:underline mb-6"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="M3 7l9 6 9-6" />
                </svg>
                coppadeiclubumbria@gmail.com
              </a>
              <p className="text-[10px] font-ui text-[#64748b] leading-relaxed">
                Sei già abilitato? Accedi dall'area{" "}
                <Link to="/admin" className="text-[#0284c7] hover:underline">
                  Login Admin
                </Link>{" "}
                per inserire i risultati.
              </p>
            </div>
            <form className="space-y-3" onSubmit={submitContact} data-testid="contact-form">
              <input
                type="text"
                required
                data-testid="contact-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 bg-[#e3edf6] border border-[#cbd5e1] rounded text-xs text-[#0f172a] placeholder-[#64748b] focus:border-[#38bdf8] focus:outline-none"
                placeholder="Circolo o Nome Capitano"
              />
              <input
                type="email"
                required
                data-testid="contact-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 bg-[#e3edf6] border border-[#cbd5e1] rounded text-xs text-[#0f172a] placeholder-[#64748b] focus:border-[#38bdf8] focus:outline-none"
                placeholder="Indirizzo Email"
              />
              <textarea
                rows={3}
                required
                data-testid="contact-message-input"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-3 py-2 bg-[#e3edf6] border border-[#cbd5e1] rounded text-xs text-[#0f172a] placeholder-[#64748b] focus:border-[#38bdf8] focus:outline-none"
                placeholder="Messaggio o richiesta supporto..."
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="w-full py-2.5 bg-[#38bdf8] text-[#0f172a] font-bold text-xs uppercase tracking-wider rounded hover:bg-[#7dd3fc] transition-colors disabled:opacity-50"
              >
                {sending ? "Invio in corso..." : "Invia Segnalazione"}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#e2e8f0] py-6 px-8 bg-[#d6e4f0] text-xs font-mono-x text-[#64748b] text-center">
        Coppa dei Club 2026-27 • Portale Ufficiale Calendari, Gironi Gold &amp; Silver • MSP Italia Umbria • Segreteria
        Generale:{" "}
        <a href="tel:3510262959" className="text-[#0284c7] hover:underline">
          3510262959
        </a>{" "}
        • Segreteria Coppa dei Club:{" "}
        <a href="mailto:coppadeiclubmspumbria@gmail.com" className="text-[#0284c7] hover:underline">
          coppadeiclubmspumbria@gmail.com
        </a>
      </footer>
    </div>
  );
}
